<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { CdrButton, IconQuestionStroke, CdrText } from '@rei/cedar';
import { win } from '@rei/browser-shim';
import type { HelpChatProps } from '@returns-page/types/index.mts';
import { renderComponent } from '@utils/helpers.mts';

const props = defineProps<HelpChatProps>();

const showChatWindow = ref(false);
const interval = ref<NodeJS.Timeout | null>(null);
const gladlyChatObserver = ref<MutationObserver | null>(null);

/* c8 ignore start */
const setStyleAttribute = (node: Element, style: string) => {
  node?.setAttribute('style', style);
};

const setDisplayStyle = (style: string) => `display: ${style} !important;`;

const hideGladlyAnswers = () => {
  const searchInChat = document.querySelector(`.${props.gladlyClasses?.answerClass}`);
  const chatLinks = document.querySelector(`.${props.gladlyClasses?.innerDivClass}`);

  if (searchInChat) {
    setStyleAttribute(searchInChat, setDisplayStyle('none'));
  }

  if (chatLinks) {
    setStyleAttribute(chatLinks, setDisplayStyle('none'));
  }
};

const addOrRemoveGladlyClass = (type: 'add' | 'remove', container: Element) => {
  container.classList[type](props.gladlyClasses.cleanslateClass);
};

const appendBeforeChat = (node: Element) => {
  const chatWithUsNode = document.querySelector(`.${props.gladlyClasses?.insertBeforeChatClass}`);
  const parentChatDiv = chatWithUsNode?.parentNode;

  parentChatDiv?.insertBefore(node, chatWithUsNode);
};

const createElementMethod = async () => {
  const component = document.createElement('div');
  component.setAttribute('class', props.gladlyClasses.containerDivClass);

  renderComponent(component, (await import('./Navigation.vue')).default);
  appendBeforeChat(component);
};

const openChat = () => {
  win.Gladly?.show();

  interval.value = setInterval(() => {
    const chatContainer = document.querySelector(`#${props.gladlyClasses?.chatContainerId}`);
    const containerDiv = document.querySelector(`.${props.gladlyClasses?.containerDivClass}`);

    if (chatContainer !== null) {
      const chatActive = document.querySelector(`.${props.gladlyClasses?.activeChatClass}`);

      chatContainer.classList.remove(props.gladlyClasses.cleanslateClass);

      setStyleAttribute(chatContainer, 'visibility: visible !important;');
      hideGladlyAnswers();

      showChatWindow.value = true;

      addOrRemoveGladlyClass(chatActive ? 'add' : 'remove', chatContainer);

      if (!chatActive) {
        createElementMethod();
      } else if (containerDiv) {
        setStyleAttribute(containerDiv, setDisplayStyle('block'));
      }

      if (interval.value) {
        clearInterval(interval.value);
      }
    }
  }, 1000);
};

const observerMethod = (targetNode: Element) => {
  // Observer function to observe the changes in gladly chat container
  const config = { attributes: true, childList: true, subtree: true };

  const callback: MutationCallback = (mutationList) => {
    mutationList.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        hideGladlyAnswers();

        const gladlyContainer = document.querySelector(`#${props.gladlyClasses?.chatContainerId}`);
        const containerDiv = document.querySelector(`.${props.gladlyClasses?.containerDivClass}`);
        const element = mutation.addedNodes[0] as HTMLElement;
        const { className } = element;

        if (className?.includes(props.gladlyClasses.containerDivClass)) {
          addOrRemoveGladlyClass('remove', gladlyContainer!);
        } else if (className?.includes(props.gladlyClasses?.activeChatClass)) {
          setStyleAttribute(containerDiv!, setDisplayStyle('none'));
          addOrRemoveGladlyClass('add', gladlyContainer!);
        } else if (className?.includes(props.gladlyClasses?.notAvailableClass)) {
          setStyleAttribute(containerDiv!, setDisplayStyle('none'));
          addOrRemoveGladlyClass('add', gladlyContainer!);
        } else if (className?.includes(props.gladlyClasses?.chatWindowClass)) {
          setStyleAttribute(element, setDisplayStyle('none'));
          showChatWindow.value = false;
        } else if (className?.includes(props.gladlyClasses?.innerDivClass)) {
          setStyleAttribute(element, setDisplayStyle('none'));
          addOrRemoveGladlyClass('remove', gladlyContainer!);
          if (containerDiv) {
            setStyleAttribute(containerDiv, setDisplayStyle('block'));
          } else {
            createElementMethod();
          }
        }
      }
    });
  };

  gladlyChatObserver.value = new MutationObserver(callback);
  gladlyChatObserver.value.observe(targetNode, config);
};
/* c8 ignore end */

onMounted(() => {
  interval.value = setInterval(() => {
    const chatContainer = document.getElementById(props.gladlyClasses?.chatContainerId);

    if (chatContainer !== null) {
      observerMethod(chatContainer);

      if (interval.value) {
        clearInterval(interval.value);
      }
    }
  }, 1000);
});

onBeforeUnmount(() => {
  gladlyChatObserver.value?.disconnect();
});
</script>

<template>
  <div
    class="chat"
    data-ui="returns-gladly-chat"
  >
    <cdr-button
      v-if="!showChatWindow"
      id="custom-gladly-chat-button"
      class="chat-button"
      data-ui="returns-gladly-button"
      @click="openChat"
    >
      <div id="with-no-active-chat">
        <icon-question-stroke class="chat-icon" />
        <cdr-text
          tag="p"
          class="chat-text"
        >
          Help
        </cdr-text>
      </div>
    </cdr-button>
  </div>
</template>
